import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { placeholderSymbolReplace } from '../../../../utils'

const dividerColor = p => p.theme.homeActionButtonBgColor ?? '#e6e9ea'
const hoverColor = p => p.theme.primaryLight

export const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`

/**
 * A result row. The divider above each row lives inside the row itself, so hovering can hide both
 * the divider above it and the one belonging to the row below without anything shifting by a pixel.
 *
 * The mobile rows run to the gutter and get no hover treatment: they are drawn straight onto the
 * search screen rather than inside a pop-up, and a sticky :hover fill on a touch screen just leaves
 * whichever row was tapped last looking selected.
 */
const Item = styled.li`
  position: relative;
  margin: ${p => (p.mobile ? '0' : '0 10px')};
  padding: ${p => (p.mobile ? '11px 0' : '11px 10px')};
  border-radius: ${p => (p.mobile ? '0' : '10px')};
  cursor: pointer;

  & + &::before {
    content: '';
    position: absolute;
    top: 0;
    left: ${p => (p.mobile ? '0' : '10px')};
    right: ${p => (p.mobile ? '0' : '10px')};
    height: 2px;
    background-color: ${dividerColor};
  }

  &:focus-visible {
    background-color: ${hoverColor};
    outline: 0;
  }

  &:focus-visible::before,
  &:focus-visible + &::before {
    background-color: transparent;
  }

  ${p =>
    !p.mobile &&
    css`
      &:hover {
        background-color: ${hoverColor(p)};
      }

      &:hover::before,
      &:hover + &::before {
        background-color: transparent;
      }
    `}
`

const ItemName = styled.div`
  font-size: 16px;
  line-height: 20px;
  color: ${p => p.theme.darkestGray ?? '#022a3a'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ItemTicker = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
`

/**
 * One selectable security: its name, with its ticker underneath when it has one the user would
 * recognise. Activated by tap, click, Enter or Space.
 *
 * @param {Object} props
 * @param {Object} props.security `{ id, securityName, ticker }`
 * @param {Function} props.onSelect called with the security's id
 * @param {boolean} [props.mobile] draws the row for the full-screen mobile search
 */
export const SecurityRow = ({ security, onSelect, mobile = false }) => {
  const { id, securityName, ticker } = security

  const handleKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onSelect(id)
    }
  }

  return (
    <Item
      mobile={mobile}
      role='option'
      tabIndex={0}
      onClick={() => onSelect(id)}
      onKeyDown={handleKeyDown}>
      <ItemName title={placeholderSymbolReplace(securityName)}>
        {placeholderSymbolReplace(securityName)}
      </ItemName>
      {ticker && <ItemTicker>{placeholderSymbolReplace(ticker)}</ItemTicker>}
    </Item>
  )
}

/**
 * A list of selectable securities.
 *
 * @param {Object} props
 * @param {Array} props.securities
 * @param {Function} props.onSelect called with the chosen security's id
 * @param {boolean} [props.mobile]
 */
export const SecurityRowList = ({ securities, onSelect, mobile = false }) => (
  <List>
    {securities.map(security => (
      <SecurityRow key={security.id} security={security} onSelect={onSelect} mobile={mobile} />
    ))}
  </List>
)
